import { AHText, Box, Circle, useScaleFactor, useTheme } from "arkh";
import { DateTime, FixedOffsetZone, IANAZone } from "luxon";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Path, Svg } from "react-native-svg";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscription,
} from "react-relay";
import { Logo } from "../../OS/Logo";
import { InfoSubscription } from "./__generated__/InfoSubscription.graphql";
import { InfoBoxScreen_StorecastScreen$key } from "./__generated__/InfoBoxScreen_StorecastScreen.graphql";

export const Info = ({
  screen,
  screenName,
}: {
  screen: InfoBoxScreen_StorecastScreen$key;
  screenName: string;
}) => {
  const sf = useScaleFactor();

  return (
    <Box
      style={{
        backgroundColor: "#F4F3F9",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
      spaceTop="xxlarge"
    >
      <Logo color="surface-cta" width={sf * 170} />
      <AHText
        style={{
          fontFamily: "poppins-display",
          fontWeight: "800",
        }}
        spaceTop="large"
        type="title-big"
      >
        <FormattedMessage
          description="Info screen title"
          defaultMessage="My name is {screenName}"
          values={{
            screenName,
          }}
        />
      </AHText>
      <AHText
        type="list-item-title"
        color="text-secondary"
        spaceTop="medium"
        spaceBottom="large"
        style={{
          textAlign: "center",
          width: "50%",
        }}
      >
        <FormattedMessage
          description="Info screen subtitle"
          defaultMessage="Check if all the screen settings are correct."
        />
      </AHText>
      <InfoBox screen={screen} />
      <Box
        style={{
          flexDirection: "row",
          flex: 1,
          width: "100%",
          maxHeight: sf * 400,
          marginTop: -sf * 50,
          alignItems: "center",
          justifyContent: "center",
        }}
        spaceBottom="large"
      >
        <picture style={{ display: "contents" }}>
          <source
            type="image/webp"
            srcSet={require("../../assets/images/info-left.webp")}
          />
          <img
            src={require("../../assets/images/info-left.png")}
            style={{
              height: "100%",
              marginLeft: sf * 110,
              marginRight: sf * 380,
            }}
          />
        </picture>
        <picture style={{ display: "contents" }}>
          <source
            type="image/webp"
            srcSet={require("../../assets/images/info-right.webp")}
          />
          <img
            src={require("../../assets/images/info-right.png")}
            style={{ height: "100%" }}
          />
        </picture>
      </Box>
    </Box>
  );
};

export const InfoBox = ({
  screen,
}: {
  screen: InfoBoxScreen_StorecastScreen$key;
}) => {
  const intl = useIntl();
  const sf = useScaleFactor();

  const data = useFragment(
    graphql`
      fragment InfoBoxScreen_StorecastScreen on StorecastScreen {
        id
        info {
          language
          languageStatus
          resolution
          resolutionStatus
          time
          timeStatus
          timeZone
          timeZoneStatus
          userAgentStatus
        }
      }
    `,
    screen
  );
  const info = data.info;

  useSubscription<InfoSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription InfoSubscription($screenId: ID!) {
            storecastScreenInfoChange(screenId: $screenId) {
              info {
                language
                languageStatus
                resolution
                resolutionStatus
                time
                timeStatus
                timeZone
                timeZoneStatus
                userAgentStatus
              }
            }
          }
        `,
        variables: { screenId: data.id },
      }),
      [data.id]
    )
  );

  const time = useMemo(
    () => (info.time ? DateTime.fromMillis(info.time).toFormat("HH:mm") : null),
    [info.time]
  );

  const timeZone = useMemo(() => {
    if (!info.timeZone) return null;
    if (IANAZone.isValidZone(info.timeZone)) {
      return IANAZone.create(info.timeZone);
    }
    return FixedOffsetZone.parseSpecifier(info.timeZoneStatus);
  }, [info.timeZone, info.timeZoneStatus]);

  const prettyTimeZone = useMemo(() => {
    if (timeZone instanceof IANAZone) {
      return timeZone.name.split("/").at(-1);
    } else if (timeZone instanceof FixedOffsetZone) {
      return `UTC ${timeZone.formatOffset(
        info.time ?? DateTime.now().toMillis(),
        "narrow"
      )}`;
    }
  }, [info.time, timeZone]);

  let formattedLanguage: string | null = info.language;
  switch (info.language) {
    case "sv":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language sv",
        defaultMessage: "Swedish",
      });
      break;

    case "en":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language en",
        defaultMessage: "English",
      });
      break;

    case "nb":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language nb",
        defaultMessage: "Norwegian",
      });
      break;
  }

  return (
    <Box
      cornerRadius="large"
      space="medium"
      color="surface-primary"
      style={{ minWidth: sf * 388 }}
    >
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status time",
          defaultMessage: "Time",
        })}
        value={time ?? "?"}
        status={info.timeStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status time zone",
          defaultMessage: "Time zone",
        })}
        value={prettyTimeZone ?? "?"}
        status={info.timeZoneStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status language",
          defaultMessage: "Language",
        })}
        value={formattedLanguage ?? "?"}
        status={info.languageStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status resolution",
          defaultMessage: "Resolution",
        })}
        value={info.resolution ?? "?"}
        status={info.resolutionStatus}
      />
      <StatusItem
        label=""
        value={intl.formatMessage({
          description: "Screen status browser version",
          defaultMessage: "Browser version",
        })}
        status={info.userAgentStatus}
        last
      />
    </Box>
  );
};

const StatusItem = ({
  label,
  value,
  status,
  last,
}: {
  label: string;
  value: string;
  status: "positive" | "semiNegative" | "negative";
  last?: boolean;
}) => {
  const sf = useScaleFactor();

  return (
    <Box
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      spaceBottom={last ? "none" : "medium"}
    >
      <AHText type="list-item-title">
        {label} <AHText type="title-small">{value}</AHText>
      </AHText>

      <StatusIcon type={status} />
    </Box>
  );
};

const StatusIcon = ({
  type,
}: {
  type: "positive" | "semiNegative" | "negative";
}) => {
  const theme = useTheme();
  const sf = useScaleFactor();

  return (
    <Circle size="smallMedium" borderWidth={sf * 2} borderColor={type}>
      <Svg viewBox="4 4 16 16" fill="none" width={sf * 16} height={sf * 16}>
        {type === "positive" ? (
          <Path
            d="M 16,8.9999699 10.5,14.5 8,12"
            stroke={theme["positive"]}
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <Path
              d="M 12,7.9709749 V 11.970925"
              stroke={theme[type]}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              d="m 12,15.971225 h 0.01"
              stroke={theme[type]}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </Svg>
    </Circle>
  );
};
